import { type CoralogixDomain, CoralogixRum } from '@coralogix/browser';
import { type FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import camelcaseKeys from 'camelcase-keys';
import type { IAccountV2 } from 'src/explore/types/shoppe';

const VALID_DOMAINS: CoralogixDomain[] = [ 'EU1', 'EU2', 'US1', 'US2', 'AP1', 'AP2', 'AP3', 'STAGING' ];
const DEFAULT_DOMAIN: CoralogixDomain = 'US2';

const IGNORED_ERROR_PATTERNS: RegExp[] = [
  /Object Not Found Matching Id/,
  /EXCLUDE LOG/,
  /Fetch error POST https:\/\/api\.segment\.io\/v1\//,
  /Google One Tap/,
  /Pinterest/i,
  /Empty reason/,
  /user is considered a bot/,
  /\[GSI_LOGGER\]/,
  /nnbucb/,
  /No assigned variation/,
  /Fullstory/,
  /SecurityError.*removeEventListener/,
  /cross-origin frame/i,
  /Permission denied to access property/,
  /^(TypeError: )?(Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.?)$/,
  /^(AbortError: )?The operation was aborted\.?$/,
  /^Script error\.?$/,
  /processingStart/,
  /Error sending segment performance metrics/,
  /window\.webkit\.messageHandlers/,
  /e\.className\.slice is not a function/,
];

let isCoralogixInited = false;

export const isCoralogixReady = (): boolean => isCoralogixInited;

const getCoralogixDomain = ( domain: string | undefined ): CoralogixDomain => {
  if ( !domain ) return DEFAULT_DOMAIN;
  if ( VALID_DOMAINS.includes( domain as CoralogixDomain )) return domain as CoralogixDomain;
  console.error( `[CoralogixLoader] Invalid VITE_RUM_CORALOGIX_DOMAIN "${domain}". Expected one of: ${VALID_DOMAINS.join( ', ' )}. Falling back to ${DEFAULT_DOMAIN}.` );
  return DEFAULT_DOMAIN;
};

interface UserSessionAccount {
  email?: string;
  first_name?: string;
  last_name?: string;
  metaStore?: { accountId: number };
}

export const tagCoralogixUserSession = ({
  account,
}: {
  account: Partial<UserSessionAccount>;
}) => {
  if ( !isCoralogixInited ) return;
  CoralogixRum.setUserContext({
    user_id: String( account.metaStore?.accountId ?? '' ),
    user_name: [ account.first_name, account.last_name ].filter( Boolean ).join( ' ' ),
    user_email: account.email,
  });
};

interface Props {
  errorOnly?: boolean;
}

export const CoralogixLoader: FC<Props> = ({ errorOnly = false }) => {
  const account = useSelector<any, IAccountV2>(( state ) =>
    state.data.account?.loading
      ? { loading: true }
      : camelcaseKeys( state.data.account?.data, { deep: true })
  );

  useEffect(() => {
    if ( !isCoralogixInited && import.meta.env.VITE_RUM_CORALOGIX_API_KEY ) {
      CoralogixRum.init({
        public_key: import.meta.env.VITE_RUM_CORALOGIX_API_KEY,
        application: import.meta.env.VITE_RUM_CORALOGIX_APPLICATION,
        version: '1.0.0',
        environment: import.meta.env.VITE_RUM_CORALOGIX_ENV,
        coralogixDomain: getCoralogixDomain( import.meta.env.VITE_RUM_CORALOGIX_DOMAIN ),
        labels: { service: import.meta.env.VITE_RUM_CORALOGIX_APPLICATION },
        ignoreErrors: IGNORED_ERROR_PATTERNS,
        maskInputTypes: [ 'password', 'email', 'tel' ],
        sessionConfig: {
          keepSessionAfterReload: true,
          onlyWithErrorConfig: { enable: errorOnly, instrumentationsToSend: {}},
        },
        instrumentations: {
          errors: true,
          fetch: true,
          xhr: true,
          web_vitals: true,
          long_tasks: true,
          interactions: true,
          resources: true,
        },
        sessionRecordingConfig: {
          enable: !errorOnly,
          autoStartSessionRecording: !errorOnly,
          sessionRecordingSampleRate: 100,
          recordConsoleEvents: true,
          recordCrossOriginIframes: true,
          errorHandler: ( error: unknown ) =>
            error instanceof DOMException &&
            error.name === 'SecurityError' &&
            error.message.includes( 'removeEventListener' ),
        },
      });
      isCoralogixInited = true;
    }
  }, [ errorOnly ]);

  useEffect(() => {
    if ( account ) {
      tagCoralogixUserSession({ account });
    }
  }, [ account ]);

  return null;
};
